import React from "react";
import { Button } from "@mui/material";
import { Link } from "react-router-dom";
import { FullPagePaperLayout } from "../Layouts/FullPagePaperLayout";
import { PageBreadcrumbs, PageTitle, PageActions, PageSubtitle } from "../Layouts/components/PageTitleContainer";
import { AllowedMovement } from "types/locationManagement/allowedMovement";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";
import { linkColDef } from "components/DataGrid/columnDefs/linkColDef";
import { actionColDef } from "components/DataGrid/columnDefs/actionColDef";
import { GridValueGetterParams } from "@mui/x-data-grid-pro";
import FeatureFlagButton from "components/FeatureFlagButton";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";

const AllowedMovementList: React.FC = () => {
  return (
    <FullPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Admin" }, { label: "Allowed Movements", path: "/allowed-movements" }]} />
      <PageTitle>Allowed Movements</PageTitle>
      <PageSubtitle>
        Define which movements are allowed between location types. By default, inventory can move freely — use allowed
        movements to explicitly permit moves between specific location types.
        <br />
        <FeatureFlagButton featureFlag="location_management_default_restricted_movement" />
      </PageSubtitle>
      <PageActions>
        <Button variant="contained" color="primary" component={Link} to="/allowed-movements/new">
          Create Allowed Movement
        </Button>
      </PageActions>
      <RevivnDatabaseDataGrid
        url="/location_management/allowed_movements"
        syncStateWithSearchParams
        columns={[
          { field: "id", headerName: "ID", width: 90, filterable: false },
          {
            field: "fromLocationType.name",
            headerName: "From Location Type",
            width: 200,
            filterable: false,
            valueGetter: (params: GridValueGetterParams<AllowedMovement>) =>
              params.row.fromLocationType?.name || "Any Location Type",
            ...linkColDef<AllowedMovement>(({ row }) => `/location-types/${row.fromLocationType?.id}/edit`),
          },
          {
            field: "toLocationType.name",
            headerName: "To Location Type",
            width: 200,
            filterable: false,
            valueGetter: (params: GridValueGetterParams<AllowedMovement>) =>
              params.row.toLocationType?.name || "Any Location Type",
            ...linkColDef<AllowedMovement>(({ row }) => `/location-types/${row.toLocationType?.id}/edit`),
          },
          {
            field: "createdAt",
            headerName: "Created At",
            width: 200,
            filterable: false,
            ...dateColDef,
          },
          {
            field: "edit",
            headerName: "Edit",
            width: 120,
            sortable: false,
            filterable: false,
            valueGetter: (_params: GridValueGetterParams) => "Edit",
            ...linkColDef<AllowedMovement>(({ row }) => `/allowed-movements/${row.id}/edit`),
          },
          {
            field: "delete",
            headerName: "Delete",
            width: 120,
            sortable: false,
            filterable: false,
            ...actionColDef<AllowedMovement>(
              ({ row }) => ({ method: "delete", action: `/allowed-movements/${row.id}/delete` }),
              <Button
                type="submit"
                variant="outlined"
                color="error"
                size="small"
                onClick={(e) => {
                  if (!confirm("Are you sure you want to delete this allowed movement?")) {
                    e.preventDefault();
                  }
                }}
              >
                Delete
              </Button>,
            ),
          },
        ]}
        pageSize={25}
      />
    </FullPagePaperLayout>
  );
};

export default AllowedMovementList;
